import { StrictMode, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createRoot } from "react-dom/client";
import {
  Activity,
  ArrowRight,
  BookOpen,
  Bot,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronDown,
  CreditCard,
  LogIn,
  LogOut,
  MessageCircle,
  PlusCircle,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
} from "lucide-react";
import {
  ApiError,
  getMe,
  getStoredToken,
  logout,
  type MeResponse,
} from "./api";
import { InkTrail } from "./components/site/InkTrail";
import { KodamaWatchers } from "./components/site/KodamaWatchers";
import "./styles.css";

type SitePage = "home" | "commands" | "info" | "terms" | "privacy";

const appOrigin = "https://app.askkodama.com";
const faqs = [
  {
    question: "What is Kodama?",
    answer:
      "Kodama is a Discord memory bot that turns your server's lore, decisions, jokes, and voice into answers that feel native to the community.",
  },
  {
    question: "Can Kodama search old Discord conversations?",
    answer:
      "Yes. Kodama indexes messages where it is active, searches server history semantically, and returns answers with source context and jump links.",
  },
  {
    question: "How is Kodama different from a normal chatbot?",
    answer:
      "Kodama answers from your community's own history and mirrors how the server actually talks, so it behaves less like a generic assistant and more like the server remembering itself.",
  },
  {
    question: "Does Kodama have privacy controls?",
    answer:
      "Yes. Members can opt out of indexing, remove specific messages with forget controls, and deleted Discord messages are removed from Kodama automatically.",
  },
];

function botInviteUrl(guildId?: string): string {
  const params = new URLSearchParams({
    client_id: "1508256939547561995",
    permissions: "67456064", // View Channel + Read History + Send Messages + Add Reactions + Embed Links + External Emojis + Change Nickname
    scope: "bot applications.commands",
  });
  if (guildId) {
    params.set("guild_id", guildId);
    params.set("disable_guild_select", "true");
  }
  return `https://discord.com/oauth2/authorize?${params.toString()}`;
}

function dashboardUrl(path = "/"): string {
  return `${appOrigin}${path}`;
}

function getInitialPage(): SitePage {
  const { pathname, search, hash } = window.location;
  if (
    pathname.startsWith("/servers") ||
    pathname.startsWith("/dashboard") ||
    pathname.startsWith("/billing") ||
    pathname.startsWith("/auth/finish") ||
    pathname.startsWith("/api/auth/callback")
  ) {
    window.location.replace(`${appOrigin}${pathname}${search}${hash}`);
  }
  if (pathname.startsWith("/commands")) return "commands";
  if (pathname.startsWith("/info")) return "info";
  if (pathname.startsWith("/privacy")) return "privacy";
  if (pathname.startsWith("/terms") || pathname.startsWith("/tos"))
    return "terms";
  return "home";
}

// Identical to app.tsx's UserMenu but navigates to app.askkodama.com for
// app-specific pages instead of doing a client-side route change.
function UserMenu({
  displayName,
  avatarUrl,
  isAdmin,
}: {
  displayName: string;
  avatarUrl: string | null;
  isAdmin?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function go(path: string) {
    setOpen(false);
    window.location.href = dashboardUrl(path);
  }

  return (
    <div ref={ref} className="user-menu-root">
      <button
        type="button"
        className="user-menu-trigger"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="" aria-hidden="true" />
        ) : (
          <span aria-hidden="true">{displayName.slice(0, 1)}</span>
        )}
        <strong>{displayName}</strong>
        <ChevronDown
          size={15}
          aria-hidden="true"
          className={open ? "chevron-open" : ""}
        />
      </button>
      {open && (
        <div className="user-menu-dropdown">
          <button type="button" onClick={() => go("/servers")}>
            <Bot size={15} aria-hidden="true" />
            My servers
          </button>
          <button type="button" onClick={() => go("/store")}>
            <CreditCard size={15} aria-hidden="true" />
            Store
          </button>
          {isAdmin && (
            <>
              <div className="user-menu-divider" />
              <button
                type="button"
                className="user-menu-admin"
                onClick={() => go("/admin")}
              >
                <ShieldCheck size={15} aria-hidden="true" />
                Admin panel
              </button>
            </>
          )}
          <div className="user-menu-divider" />
          <button
            type="button"
            className="user-menu-signout"
            onClick={() =>
              logout()
                .catch(() => null)
                .finally(() => {
                  setOpen(false);
                  window.location.reload();
                })
            }
          >
            <LogOut size={15} aria-hidden="true" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function MarketingNav({
  me,
  meLoading = false,
  onNavigate,
}: {
  me: MeResponse | null;
  meLoading?: boolean;
  onNavigate: (page: SitePage) => void;
}) {
  const displayName = me?.user.global_name ?? me?.user.username ?? "User";
  const avatarUrl = me?.user.avatar
    ? `https://cdn.discordapp.com/avatars/${me.user.id}/${me.user.avatar}.png?size=64`
    : null;

  return (
    <header className="marketing-nav">
      <button
        className="brand-button"
        type="button"
        onClick={() => onNavigate("home")}
      >
        <span className="public-brand">
          <img src="/kodama_logo.png" alt="" aria-hidden="true" />
          <strong>Kodama</strong>
        </span>
      </button>
      <nav aria-label="Public navigation">
        <button type="button" onClick={() => onNavigate("commands")}>
          <BookOpen size={17} aria-hidden="true" />
          Commands
        </button>
        <button type="button" onClick={() => onNavigate("info")}>
          <Activity size={17} aria-hidden="true" />
          Status
        </button>
        <a href={dashboardUrl("/store")}>
          <CreditCard size={17} aria-hidden="true" />
          Store
        </a>
        <a href={botInviteUrl()}>
          <PlusCircle size={17} aria-hidden="true" />
          Invite
        </a>
      </nav>
      <div className="marketing-user">
        {meLoading && !me ? (
          <span className="nav-auth-loading" aria-label="Loading" />
        ) : me ? (
          <UserMenu
            displayName={displayName}
            avatarUrl={avatarUrl}
            isAdmin={me.isAdmin}
          />
        ) : (
          <a href={dashboardUrl("/servers")}>
            <LogIn size={17} aria-hidden="true" />
            Sign in
          </a>
        )}
      </div>
    </header>
  );
}

function MarketingFooter({
  onNavigate,
}: {
  onNavigate: (page: SitePage) => void;
}) {
  return (
    <footer className="marketing-footer">
      <div className="footer-brand">
        <span className="public-brand footer-brand-mark">
          <img src="/kodama_logo.png" alt="" aria-hidden="true" />
          <strong>Kodama</strong>
        </span>
        <p>Server lore with receipts.</p>
        <p className="footer-copy-line">
          &copy; {new Date().getFullYear()} Kodama. All rights reserved.
        </p>
      </div>
      <nav className="footer-links" aria-label="Footer navigation">
        <div>
          <h4>Product</h4>
          <button type="button" onClick={() => onNavigate("commands")}>
            Commands
          </button>
          <a href={botInviteUrl()}>Add to Discord</a>
          <a href={dashboardUrl("/servers")}>Dashboard</a>
          <a
            href="https://discord.gg/xT9vgkTHqF"
            target="_blank"
            rel="noopener noreferrer"
          >
            Support server
          </a>
        </div>
        <div>
          <h4>Legal</h4>
          <button type="button" onClick={() => onNavigate("terms")}>
            Terms of Service
          </button>
          <button type="button" onClick={() => onNavigate("privacy")}>
            Privacy Policy
          </button>
        </div>
      </nav>
    </footer>
  );
}

function DiscordMockup() {
  const mockupRef = useRef<HTMLDivElement>(null);
  const [messagesVisible, setMessagesVisible] = useState(false);

  useEffect(() => {
    const node = mockupRef.current;
    if (!node) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReducedMotion || !("IntersectionObserver" in window)) {
      setMessagesVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setMessagesVisible(true);
        observer.disconnect();
      },
      { threshold: 0.32 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={mockupRef}
      className={`discord-mockup ${messagesVisible ? "dm-visible" : ""}`}
      aria-hidden="true"
    >
      <div className="dm-titlebar">
        <span className="dm-dot" />
        <span className="dm-dot" />
        <span className="dm-dot" />
        <span className="dm-titlebar-name"># general</span>
      </div>
      <div className="dm-header">
        <span className="dm-channel-hash">#</span>
        <span className="dm-channel-name">general</span>
        <span className="dm-header-sep">|</span>
        <span className="dm-header-topic">server hub</span>
      </div>
      <div className="dm-messages">
        <div className="dm-message">
          <div className="dm-avatar dm-avatar-purple">xS</div>
          <div className="dm-body">
            <span className="dm-name dm-name-purple">xSkull_Reign</span>
            <span className="dm-ts">Today at 4:12 PM</span>
            <div className="dm-text">
              who won the s1 tourney again, i know there was lore
            </div>
          </div>
        </div>
        <div className="dm-message">
          <div className="dm-avatar dm-avatar-red">jd</div>
          <div className="dm-body">
            <span className="dm-name dm-name-red">jake_dev</span>
            <span className="dm-ts">Today at 4:13 PM</span>
            <div className="dm-text">no idea bro that was forever ago 😭</div>
          </div>
        </div>
        <div className="dm-message dm-kodama-msg">
          <div className="dm-avatar dm-avatar-kodama">
            <img src="/default_bot_pfp.png" alt="" />
          </div>
          <div className="dm-body">
            <span className="dm-name dm-name-kodama">
              Kodama
              <span className="dm-bot-tag">BOT</span>
            </span>
            <span className="dm-ts">Today at 4:13 PM</span>
            <div className="dm-text">
              Ryder. OT finals, reverse sweep, then everyone spammed "greatest
              upset in server history" for like a week 📌
            </div>
          </div>
        </div>
        <div className="dm-message">
          <div className="dm-avatar dm-avatar-purple">xS</div>
          <div className="dm-body">
            <span className="dm-name dm-name-purple">xSkull_Reign</span>
            <span className="dm-ts">Today at 4:14 PM</span>
            <div className="dm-text">💀 it talks like us too</div>
          </div>
        </div>
        <div className="dm-slash-row">
          <span className="dm-slash-icon">/</span>
          <span className="dm-slash-cmd">lore search</span>
          <span className="dm-slash-arg">tournament winner</span>
        </div>
      </div>
    </div>
  );
}

function LandingPage({
  me,
  meLoading,
  onNavigate,
}: {
  me: MeResponse | null;
  meLoading: boolean;
  onNavigate: (page: SitePage) => void;
}) {
  return (
    <main className="marketing-shell">
      <MarketingNav me={me} meLoading={meLoading} onNavigate={onNavigate} />
      <InkTrail />
      <KodamaWatchers />

      <section className="hero-new">
        <div className="hero-new-content">
          <h1 className="hero-new-headline">
            Server memory,
            <br />
            in your
            <br />
            <span className="hero-headline-accent">server&apos;s voice.</span>
          </h1>
          <p className="hero-new-body">
            Kodama finds the old messages, decisions, jokes, and context that
            shaped your community, then answers with receipts in a voice that
            feels like it belongs there.
          </p>
          <div className="hero-new-ctas">
            <a href={botInviteUrl()} className="primary-button">
              <PlusCircle size={18} aria-hidden="true" />
              Add to Discord, it&apos;s free
            </a>
            <a className="hero-cta-ghost" href={dashboardUrl("/servers")}>
              Open dashboard
              <ArrowRight size={16} aria-hidden="true" />
            </a>
          </div>
        </div>
        <div className="hero-new-demo">
          <DiscordMockup />
        </div>
      </section>

      <section className="features-new" aria-label="Features">
        <div className="features-header">
          <p className="eyebrow">What Kodama does</p>
          <h2 className="features-headline">
            More than search. A memory that sounds like the room.
          </h2>
        </div>
        <div className="features-grid-new">
          {[
            [
              <BookOpen size={20} aria-hidden="true" />,
              "Memory with receipts",
              "Searches old messages, decisions, jokes, and references so members get the context without asking veterans again.",
              "/lore search tournament winner",
              "feature-icon-mint",
            ],
            [
              <Sparkles size={20} aria-hidden="true" />,
              "A native voice",
              "Learns your server's slang, rhythm, emoji habits, and inside references so replies feel like they came from the community.",
              "/settings personality mirror",
              "feature-icon-cyan",
            ],
            [
              <ShieldCheck size={20} aria-hidden="true" />,
              "Privacy first",
              "Members can opt out or hard-delete specific messages. Deleted Discord messages disappear automatically.",
              "/optout",
              "feature-icon-violet",
            ],
            [
              <CalendarClock size={20} aria-hidden="true" />,
              "Recaps with texture",
              "Summarizes the day's conversations, major moments, active people, and recurring bits in the server's own style.",
              "/recap today",
              "feature-icon-mint",
            ],
            [
              <MessageCircle size={20} aria-hidden="true" />,
              "Mention to ask",
              "@mention Kodama anywhere and it pulls the right people, events, phrases, and callbacks from server memory.",
              "@Kodama what's ryder's deal",
              "feature-icon-cyan",
            ],
            [
              <SlidersHorizontal size={20} aria-hidden="true" />,
              "Mod controls",
              "Personality, flame mode, recap schedule, and behavior toggles stay in admin hands.",
              "/settings flame on",
              "feature-icon-violet",
            ],
          ].map(([icon, title, body, command, iconClass]) => (
            <article className="feature-item" key={String(title)}>
              <div className={`feature-item-icon ${String(iconClass)}`}>
                {icon}
              </div>
              <h3>{String(title)}</h3>
              <p>{String(body)}</p>
              <code>{String(command)}</code>
            </article>
          ))}
        </div>
      </section>

      <section className="faq-section" aria-labelledby="faq-heading">
        <div className="faq-header">
          <p className="eyebrow">FAQ</p>
          <h2 id="faq-heading" className="faq-headline">
            Built for servers with a real culture.
          </h2>
        </div>
        <div className="faq-grid">
          {faqs.map((faq) => (
            <article className="faq-item" key={faq.question}>
              <h3>{faq.question}</h3>
              <p>{faq.answer}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="pricing-section" aria-label="Plans">
        <div className="pricing-header">
          <p className="eyebrow">Plans</p>
          <h2 className="pricing-headline">
            free to start. pay when it matters.
          </h2>
          <p className="pricing-subhead">
            Kodama runs in your server for free from day one. upgrade when you
            want deeper archive search, more sourced answers, stronger server
            memory, or a custom presence.
          </p>
        </div>
        <div className="pricing-grid">
          <div className="pricing-card">
            <div className="pricing-card-head">
              <strong>Free</strong>
              <span className="pricing-price">$0</span>
              <span className="pricing-period">forever</span>
            </div>
            <ul className="pricing-features">
              <li>
                <Check size={14} aria-hidden="true" />
                Kodama interactions (20/day)
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                Lore search (5/day)
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                /whois (5/day)
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                Privacy controls
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                Ask Kodama (5/day)
              </li>
            </ul>
            <a href={botInviteUrl()} className="pricing-cta-ghost">
              Add to Discord
            </a>
          </div>

          <div className="pricing-card pricing-card--featured">
            <div className="pricing-card-badge">most popular</div>
            <div className="pricing-card-head">
              <strong>Index</strong>
              <span className="pricing-price">$9.99</span>
              <span className="pricing-period">/month</span>
            </div>
            <ul className="pricing-features">
              <li>
                <Check size={14} aria-hidden="true" />
                Everything in Free
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                Ask Kodama (20/day)
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                Organic replies
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                Server conversation history scan
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                /recap (3/day)
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                /whois (20/day)
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                Bot profile &amp; banner
              </li>
            </ul>
            <a
              href={dashboardUrl("/store")}
              className="primary-button pricing-cta"
            >
              Get Index
            </a>
          </div>

          <div className="pricing-card">
            <div className="pricing-card-head">
              <strong>Chronicle</strong>
              <span className="pricing-price">$19.99</span>
              <span className="pricing-period">/month</span>
            </div>
            <ul className="pricing-features">
              <li>
                <Check size={14} aria-hidden="true" />
                Everything in Index
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                No 20/day Kodama interaction cap for anyone in servers you own
                or manage
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                Ask Kodama unlimited
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                Image &amp; GIF understanding
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                /recap (10/day)
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                Deep Discord archive search
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                Inside joke detection
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                Reports feed
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                Bot description field
              </li>
            </ul>
            <a href={dashboardUrl("/store")} className="pricing-cta-ghost">
              Get Chronicle
            </a>
          </div>
        </div>
      </section>

      <section className="cta-new" aria-label="Get started">
        <div className="cta-new-inner">
          <img
            src="/kodama_logo.png"
            alt=""
            className="cta-mascot"
            aria-hidden="true"
          />
          <div className="cta-new-copy">
            <h2>Let your server remember itself.</h2>
            <p>
              Free to add. No credit card. Works in any server where the lore,
              decisions, jokes, and recurring bits deserve to stick around.
            </p>
          </div>
          <a href={botInviteUrl()} className="primary-button cta-new-btn">
            <PlusCircle size={18} aria-hidden="true" />
            Add Kodama to Discord
          </a>
        </div>
      </section>

      <div className="hero-mascot hero-new-mascot" aria-hidden="true">
        <img src="/kodama_logo.png" alt="" />
      </div>

      <MarketingFooter onNavigate={onNavigate} />
    </main>
  );
}

function CommandsPage({
  me,
  meLoading,
  onNavigate,
}: {
  me: MeResponse | null;
  meLoading: boolean;
  onNavigate: (page: SitePage) => void;
}) {
  const commands = [
    ["/lore search", "Find old jokes, decisions, moments, and references."],
    ["/lore add", "Let mods pin a memory manually."],
    ["/lore list", "Let mods review recently pinned memories."],
    ["/whois", "Summarize a member's server profile and notable moments."],
    ["/timeline", "List saved server moments chronologically."],
    ["/recap", "Generate a daily, weekly, or monthly server summary."],
    [
      "/forget",
      "Remove your indexed message, or any indexed message as a mod.",
    ],
    ["/optout", "Exclude a member from indexing and summaries."],
    ["/export", "Download your indexed data for the current server."],
    ["/help", "Show commands, limits, dashboard, and support links."],
    ["/privacy", "Explain indexing, opt-out, forget, and policy links."],
    ["/settings", "Tune personality, flame mode, and recap delivery."],
  ];

  return (
    <main className="marketing-shell">
      <MarketingNav me={me} meLoading={meLoading} onNavigate={onNavigate} />
      <section className="info-shell">
        <div className="info-heading">
          <p className="eyebrow">Info</p>
          <h2>Status and commands.</h2>
        </div>
        <section className="status-grid" aria-label="Kodama status">
          <article>
            <CheckCircle2 size={20} aria-hidden="true" />
            <strong>Bot gateway</strong>
            <span>Online</span>
          </article>
          <article>
            <ShieldCheck size={20} aria-hidden="true" />
            <strong>Privacy actions</strong>
            <span>Forget and opt-out enabled</span>
          </article>
          <article>
            <CalendarClock size={20} aria-hidden="true" />
            <strong>Recaps</strong>
            <span>Scheduled per server</span>
          </article>
        </section>
        <section className="commands-table" aria-label="Kodama commands">
          {commands.map(([command, description]) => (
            <article key={command}>
              <code>{command}</code>
              <p>{description}</p>
            </article>
          ))}
        </section>
      </section>
      <MarketingFooter onNavigate={onNavigate} />
    </main>
  );
}

function LegalPage({
  title,
  children,
  me,
  meLoading,
  onNavigate,
}: {
  title: string;
  children: ReactNode;
  me: MeResponse | null;
  meLoading: boolean;
  onNavigate: (page: SitePage) => void;
}) {
  return (
    <main className="marketing-shell">
      <MarketingNav me={me} meLoading={meLoading} onNavigate={onNavigate} />
      <div className="legal-shell">
        <div className="legal-header">
          <p className="eyebrow">Legal</p>
          <h1>{title}</h1>
          <p>Effective date: May 26, 2026</p>
        </div>
        <div className="legal-body">{children}</div>
      </div>
      <MarketingFooter onNavigate={onNavigate} />
    </main>
  );
}

function LegalSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="legal-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function TermsPage({
  me,
  meLoading,
  onNavigate,
}: {
  me: MeResponse | null;
  meLoading: boolean;
  onNavigate: (page: SitePage) => void;
}) {
  return (
    <LegalPage
      title="Terms of Service"
      me={me}
      meLoading={meLoading}
      onNavigate={onNavigate}
    >
      <LegalSection title="1. Acceptance of Terms">
        <p>
          By adding Kodama to your Discord server or using Kodama&apos;s
          dashboard, you agree to these Terms of Service. If you do not agree,
          do not use the service.
        </p>
      </LegalSection>
      <LegalSection title="2. Description of Service">
        <p>
          Kodama indexes server messages, detects lore moments and inside jokes,
          generates recaps, builds member profiles, and provides
          personality-driven replies based on server memory.
        </p>
      </LegalSection>
      <LegalSection title="3. Privacy Controls">
        <ul>
          <li>/optout excludes a member from future indexing and summaries.</li>
          <li>/optin re-enables indexing after opting out.</li>
          <li>/forget permanently removes a specific message from memory.</li>
          <li>
            Deleted Discord messages are removed from Kodama automatically.
          </li>
        </ul>
      </LegalSection>
      <LegalSection title="4. Acceptable Use">
        <p>
          You may not use Kodama to harass people, violate Discord&apos;s terms,
          bypass privacy controls, reverse-engineer the service, or use the
          service for unlawful activity.
        </p>
      </LegalSection>
      <LegalSection title="5. Data Processing Limits">
        <p>
          Kodama does not utilize message content obtained through the Discord
          API to train, pre-train, or fine-tune foundational machine learning
          models or Large Language Models (LLMs). All data processing is
          strictly limited to localized semantic search indices and temporary
          in-context retrieval to fulfill user commands.
        </p>
      </LegalSection>
      <LegalSection title="6. Service Availability">
        <p>
          Kodama is provided as-is. We may modify or discontinue the service,
          and we do not guarantee uninterrupted availability or perfect lore
          accuracy.
        </p>
      </LegalSection>
      <LegalSection title="7. Contact">
        <p>
          For questions, reach us through the{" "}
          <a
            href="https://discord.gg/xT9vgkTHqF"
            target="_blank"
            rel="noopener noreferrer"
          >
            Discord support server
          </a>
          .
        </p>
      </LegalSection>
    </LegalPage>
  );
}

function PrivacyPage({
  me,
  meLoading,
  onNavigate,
}: {
  me: MeResponse | null;
  meLoading: boolean;
  onNavigate: (page: SitePage) => void;
}) {
  return (
    <LegalPage
      title="Privacy Policy"
      me={me}
      meLoading={meLoading}
      onNavigate={onNavigate}
    >
      <LegalSection title="1. Introduction">
        <p>
          Kodama collects and protects information from Discord servers where it
          is installed. User-controlled deletion and opt-out tools are part of
          the core product.
        </p>
      </LegalSection>
      <LegalSection title="2. What We Collect">
        <ul>
          <li>Message content in channels where Kodama is active.</li>
          <li>
            Message metadata such as channel IDs, timestamps, and reactions.
          </li>
          <li>Server IDs, names, and admin-selected configuration.</li>
          <li>
            Discord user IDs and short-lived session tokens for dashboard
            sign-in.
          </li>
        </ul>
      </LegalSection>
      <LegalSection title="3. What We Do Not Collect">
        <ul>
          <li>Discord email addresses or passwords.</li>
          <li>Direct messages.</li>
          <li>Voice channel audio or video.</li>
          <li>Payment information.</li>
        </ul>
      </LegalSection>
      <LegalSection title="4. How We Use Data">
        <p>
          Data powers semantic lore search, contextual replies, daily recaps,
          member profiles, privacy audit logs, and server admin controls.
        </p>
        <p>
          Kodama does not utilize message content obtained through the Discord
          API to train, pre-train, or fine-tune foundational machine learning
          models or Large Language Models (LLMs). All data processing is
          strictly limited to localized semantic search indices and temporary
          in-context retrieval to fulfill user commands.
        </p>
      </LegalSection>
      <LegalSection title="5. Third-Party Services">
        <p>
          Kodama uses Voyage AI for embeddings, Google Gemini and Anthropic
          Claude for generated text, and Discord APIs for server access and bot
          responses.
        </p>
      </LegalSection>
      <LegalSection title="6. Retention and Requests">
        <p>
          Server data is retained while Kodama is installed. Opt-out and forget
          requests delete matching indexed data, and server deletion requests
          can be made through support.
        </p>
      </LegalSection>
    </LegalPage>
  );
}

function SiteApp() {
  const [page, setPage] = useState<SitePage>(getInitialPage);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [meLoading, setMeLoading] = useState(() => !!getStoredToken());

  useEffect(() => {
    // Only attempt getMe if there's a token in localStorage or the shared cookie
    if (!getStoredToken()) return;
    getMe()
      .then(setMe)
      .catch((err: unknown) => {
        // Session expired or invalid — don't show signed-in state
        if (err instanceof ApiError && err.status === 401) return;
      })
      .finally(() => setMeLoading(false));

    function syncFromLocation() {
      setPage(getInitialPage());
    }
    window.addEventListener("popstate", syncFromLocation);
    return () => window.removeEventListener("popstate", syncFromLocation);
  }, []);

  function navigate(nextPage: SitePage): void {
    const pathMap: Record<SitePage, string> = {
      home: "/",
      commands: "/commands",
      info: "/info",
      terms: "/terms",
      privacy: "/privacy",
    };
    window.history.pushState(null, "", pathMap[nextPage]);
    setPage(nextPage);
  }

  if (page === "commands" || page === "info")
    return <CommandsPage me={me} meLoading={meLoading} onNavigate={navigate} />;
  if (page === "terms")
    return <TermsPage me={me} meLoading={meLoading} onNavigate={navigate} />;
  if (page === "privacy")
    return <PrivacyPage me={me} meLoading={meLoading} onNavigate={navigate} />;
  return <LandingPage me={me} meLoading={meLoading} onNavigate={navigate} />;
}

export function mountApp(): void {
  createRoot(document.getElementById("root") as HTMLElement).render(
    <StrictMode>
      <SiteApp />
    </StrictMode>,
  );
}
